/**
 * Export handlers for the launch screens (G2.4, G2 test d).
 *
 * One per data route. Each returns the CURRENT view — G2.4's fidelity rule is that an
 * export reflects the filters, sort, column set and drill level the planner is looking
 * at, not the unfiltered underlying query — and each carries all nine provenance
 * fields, which the type makes non-optional (G2 test e).
 *
 * Numbers leave as `raw`. There is no path in `ExportCell` that carries a display
 * string for a number, so "full precision, unrounded, never a display string" is a type
 * property rather than a discipline.
 *
 * Every sheet is read from the same operations the screen reads, scoped by the same
 * `?option=` and the same newest allocation version (`./version.tsx`). With no option in
 * the URL there is no version and the sheet has no rows — nothing is defaulted. The three
 * sheets used to be fixture rows; they were rewired when the fixture panels were removed on
 * 2026-09-27 (F8).
 *
 * The shipment export matters more than most. A DC or a WMS integrator downstream of
 * this screen needs store, option, size, units and ship-week at full precision with the
 * reason code attached, and G2.4's rule that Excel is a *destination* and never a
 * *step* is what keeps this an outbound extract rather than a planning stage.
 */

import { collectPages, callOperation } from '@/api';
import {
  launchListProposals, listVersions, sizeRunReport,
  type AllocationVersionModel,
} from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, PERIMETER_NOT_RECORDED, buildVersion } from '@/export';
import { PERSON_NAME_DISPLAY, SIZE_EXPORT_COLUMNS, SIZE_EXPORT_KEYS, decidedByExportCell, sizeExportCells } from '@/planning';
import type { ExportCell, ExportRequest, Provenance } from '@/export';

import type { ExportScope } from '../manifest';

import { LAUNCH_DISPOSITION_KEY, decimal, inShipmentOrder, latestVersion, reportFor, toShipmentLines } from './api';
import { NO_OPTION, OPTION_PARAM } from './context';

/** The build that produced the file — `buildVersion` in `@/export`, `docs/adr/9007`. */
const PLATFORM_VERSION = buildVersion();

const provenance = (
  measures: readonly { measure: string; definition: string }[],
): Provenance => ({
  // The screen does not record the perimeter it read yet; no filter list is claimed (ADR 9007).
  filters: PERIMETER_NOT_RECORDED,
  hierarchyNode: NOT_PUBLISHED,
  timeWindow: NOT_PUBLISHED,
  scenario: NOT_PUBLISHED,
  planVersionId: NOT_PUBLISHED,
  measureDefinitions: measures.map((entry) => ({
    measure: entry.measure,
    definition: entry.definition,
    registerVersion: '',
  })),
  generatedAt: new Date().toISOString(),
  generatedBy: NO_PRINCIPAL,
  platformVersion: PLATFORM_VERSION,
});

/**
 * The allocation version the screen is showing: the newest run for the option in the URL,
 * or `undefined` when no option is chosen or the option has no run. Never a default.
 */
async function versionInScope(scope?: ExportScope): Promise<AllocationVersionModel | undefined> {
  const optionId = scope?.params[OPTION_PARAM] ?? NO_OPTION;
  if (optionId.trim() === '') return undefined;
  const versions = await collectPages(listVersions, { query: { option_id: optionId } });
  return latestVersion(versions);
}

const versionFilter = (version: AllocationVersionModel | undefined) => (
  version === undefined
    ? []
    : [{ field: 'allocation_version_id', operator: 'equals' as const, value: version.allocation_version_id }]
);

/**
 * The shipment list — reqs/03 §1's definition of the engine's output, exported whole.
 * Every line carries its reason code, because reqs/03 §8 requires it on the line and an
 * export that dropped it would make the approval trail unauditable outside the product.
 */
export async function shipmentListExport(scope?: ExportScope): Promise<ExportRequest> {
  const version = await versionInScope(scope);
  const lines = version === undefined
    ? []
    // In the order the perimeter below declares: door, then the size's place in the run.
    : inShipmentOrder(toShipmentLines(await collectPages(launchListProposals, {
      query: { allocation_version_id: version.allocation_version_id },
    })));
  const rows: ExportCell[][] = lines.map((line) => [
    { kind: 'text', value: line.storeId },
    // The size as the platform named it, the key kept as an identifier (ADR 9027).
    ...sizeExportCells(line.size),
    { kind: 'integer', raw: line.units },
    { kind: 'text', value: line.shipWeek },
    { kind: 'label', value: line.reasonCode, code: line.reasonCode },
    // An impact whose decimal did not parse leaves as missing, never as zero.
    line.expectedImpact === null
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'money', raw: line.expectedImpact, currency: line.currencyCode },
    // The recorded decision and who took it (bata-platform ADR 5001): pending, and nobody, until one is.
    { kind: 'label', value: line.disposition ?? 'pending', code: line.disposition ?? 'pending' },
    decidedByExportCell({
      disposition: line.disposition ?? null, actor: line.dispositionActor ?? null, actorName: line.dispositionActorName ?? null,
      actorNameAbsentReason: line.dispositionActorNameAbsentReason ?? null,
    }),
  ]);
  return {
    id: 'launch.plan.grid',
    titleKey: 'launch.plan.title',
    columns: [
      { key: 'store_id', headerKey: 'launch.column.storeId', kind: 'text', descriptionKey: 'launch.columnGuide.plan.storeId' },
      ...SIZE_EXPORT_COLUMNS,
      { key: 'units', headerKey: 'launch.column.units', kind: 'integer', descriptionKey: 'launch.columnGuide.plan.units' },
      { key: 'ship_week', headerKey: 'launch.column.shipWeek', kind: 'text', descriptionKey: 'launch.columnGuide.plan.shipWeek' },
      { key: 'reason_code', headerKey: 'launch.column.reasonCode', kind: 'label', descriptionKey: 'launch.columnGuide.plan.reasonCode' },
      { key: 'expected_impact', headerKey: 'launch.column.expectedImpact', kind: 'money', descriptionKey: 'launch.columnGuide.plan.expectedImpact' },
      {
        key: 'disposition', headerKey: 'launch.column.disposition', kind: 'label',
        display: (code, translate) => { const key = LAUNCH_DISPOSITION_KEY[code]; return key === undefined ? code : translate(key); },
        descriptionKey: 'launch.columnGuide.plan.disposition',
      },
      {
        key: 'decided_by', headerKey: 'person.decidedBy.column', kind: 'label', display: PERSON_NAME_DISPLAY, descriptionKey: 'person.decidedBy.help',
        absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'person.decidedBy.absent.pending' } },
      },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      {
        measure: 'launch.allocated_units',
        definition: 'Units despatched to a door for an option and size in the launch ship week.',
      },
      {
        measure: 'launch.realised_margin',
        definition: 'Expected full-price margin realised, net of expected markdown exposure, over the initial selling window.',
      },
    ]),
    perimeter: {
      filters: versionFilter(version),
      sort: [{ key: 'store_id', direction: 'asc' }, { key: 'size_run_position', direction: 'asc' }],
      visibleColumns: ['store_id', ...SIZE_EXPORT_KEYS, 'units', 'ship_week', 'reason_code', 'expected_impact', 'disposition', 'decided_by'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * Gate D3's measurement for the version on screen — `GET /launch/size-run-report`, the row
 * for that version only. The share leaves as its parsed decimal, or missing; never zero.
 */
export async function sizeRunExport(scope?: ExportScope): Promise<ExportRequest> {
  const version = await versionInScope(scope);
  const row = version === undefined
    ? undefined
    : reportFor(
      await callOperation(sizeRunReport, { query: { allocation_version_id: version.allocation_version_id } }),
      version.allocation_version_id,
    );
  const share = row === undefined ? null : decimal(row.complete_run_share);
  const rows: ExportCell[][] = row === undefined ? [] : [[
    { kind: 'text', value: row.allocation_version_id },
    { kind: 'integer', raw: row.doors_served },
    { kind: 'integer', raw: row.doors_with_complete_core_run },
    { kind: 'integer', raw: row.doors_dropped },
    share === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: share },
    { kind: 'integer', raw: row.units_redistributed },
  ]];
  return {
    id: 'launch.sizeRun.report',
    titleKey: 'launch.sizeRun.title',
    columns: [
      { key: 'allocation_version_id', headerKey: 'launch.run.versionId', kind: 'text', descriptionKey: 'launch.columnGuide.sizeRun.versionId' },
      { key: 'doors_served', headerKey: 'launch.sizeRun.doorsServed', kind: 'integer', descriptionKey: 'launch.columnGuide.sizeRun.doorsServed' },
      { key: 'doors_with_complete_core_run', headerKey: 'launch.sizeRun.doorsComplete', kind: 'integer', descriptionKey: 'launch.columnGuide.sizeRun.doorsComplete' },
      { key: 'doors_dropped', headerKey: 'launch.sizeRun.doorsDropped', kind: 'integer', descriptionKey: 'launch.columnGuide.sizeRun.doorsDropped' },
      { key: 'complete_run_share', headerKey: 'launch.sizeRun.completeShare', kind: 'number', descriptionKey: 'launch.columnGuide.sizeRun.completeShare' },
      { key: 'units_redistributed', headerKey: 'launch.sizeRun.unitsRedistributed', kind: 'integer', descriptionKey: 'launch.columnGuide.sizeRun.unitsRedistributed' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      {
        measure: 'launch.core_run_complete',
        definition: 'Share of served doors opening the season with at least one unit in every core size.',
      },
    ]),
    perimeter: {
      filters: versionFilter(version),
      sort: [],
      visibleColumns: ['allocation_version_id', 'doors_served', 'doors_with_complete_core_run', 'doors_dropped', 'complete_run_share', 'units_redistributed'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The realised hold-back of the version on screen — `AllocationVersionModel`'s launch and
 * withheld units, and the fraction the screen derives from them (undefined over a zero
 * launch, never zero).
 */
export async function withholdingExport(scope?: ExportScope): Promise<ExportRequest> {
  const version = await versionInScope(scope);
  const rows: ExportCell[][] = version === undefined ? [] : [[
    { kind: 'text', value: version.allocation_version_id },
    { kind: 'integer', raw: version.launch_units },
    { kind: 'integer', raw: version.withheld_units },
    version.launch_units === 0
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'percent', raw: version.withheld_units / version.launch_units },
  ]];
  return {
    id: 'launch.withholding.realised',
    titleKey: 'launch.withholding.title',
    columns: [
      { key: 'allocation_version_id', headerKey: 'launch.run.versionId', kind: 'text', descriptionKey: 'launch.columnGuide.sizeRun.versionId' },
      { key: 'launch_units', headerKey: 'launch.plan.launchUnits', kind: 'integer', descriptionKey: 'launch.columnGuide.withholding.launchUnits' },
      { key: 'withheld_units', headerKey: 'launch.plan.withheldUnits', kind: 'integer', descriptionKey: 'launch.columnGuide.withholding.withheldUnits' },
      { key: 'hold_fraction', headerKey: 'launch.withholding.realisedFraction', kind: 'percent', descriptionKey: 'launch.columnGuide.withholding.holdFraction' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([
      {
        measure: 'launch.withheld_units',
        definition: 'Units held at the DC for the week-3 chase rather than pushed on day one.',
      },
    ]),
    perimeter: {
      filters: versionFilter(version),
      sort: [],
      visibleColumns: ['allocation_version_id', 'launch_units', 'withheld_units', 'hold_fraction'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}
